import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import validator from 'validator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { createVoucherCode, updateVoucherCode } from '../redux/slices/vouchersSlice';

const EMPTY_FORM = {
  code: '',
  assignedEmail: '',
  maxRedemptions: 1,
  active: true,
};

export default function VoucherCodeDrawer({ open, onClose, voucherId, voucherCode }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = Boolean(voucherCode);
  const currentUsage = Number(voucherCode?.reservedCount || 0) + Number(voucherCode?.redeemedCount || 0);

  useEffect(() => {
    if (!open) return;
    setForm(
      voucherCode
        ? {
            code: voucherCode.code,
            assignedEmail: voucherCode.assignedEmail || '',
            maxRedemptions: voucherCode.maxRedemptions,
            active: voucherCode.active,
          }
        : EMPTY_FORM
    );
  }, [open, voucherCode]);

  const validation = useMemo(
    () => ({
      code: !isEditing && !form.code.trim(),
      email: Boolean(form.assignedEmail.trim() && !validator.isEmail(form.assignedEmail.trim())),
      maxRedemptions:
        !Number.isInteger(Number(form.maxRedemptions)) ||
        Number(form.maxRedemptions) < 1 ||
        Number(form.maxRedemptions) < currentUsage,
    }),
    [currentUsage, form, isEditing]
  );
  const isInvalid = Object.values(validation).some(Boolean);

  const handleClose = () => {
    if (isSaving) return;
    setForm(EMPTY_FORM);
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isInvalid) return;

    const payload = {
      active: form.active,
      maxRedemptions: Number(form.maxRedemptions),
      assignedEmail: form.assignedEmail.trim() || null,
    };
    if (!isEditing) payload.code = form.code.trim();

    setIsSaving(true);
    try {
      await dispatch(
        isEditing
          ? updateVoucherCode({ voucherId, codeId: voucherCode.id, payload })
          : createVoucherCode({ voucherId, payload })
      ).unwrap();
      toast.success(t(isEditing ? 'Voucher code updated successfully' : 'Voucher code created successfully'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setIsSaving(false);
      handleClose();
    } catch (error) {
      const errorType = error?.errorType;
      toast.error(
        t(
          errorType === 'VOUCHER_CODE_ALREADY_EXISTS'
            ? 'This voucher code already exists'
            : errorType === 'VOUCHER_CODE_EMAIL_LOCKED'
            ? 'Assigned email cannot be changed after the code has been used'
            : 'Voucher code could not be saved'
        ),
        { position: toast.POSITION.BOTTOM_RIGHT }
      );
      setIsSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Toolbar />
      <Toolbar>
        <Typography variant="button">{t(isEditing ? 'Edit voucher code' : 'Add voucher code')}</Typography>
      </Toolbar>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} sx={{ width: 400, px: 2, pb: 2 }}>
        <TextField
          required
          fullWidth
          margin="normal"
          size="small"
          label={t('Code')}
          value={form.code}
          disabled={isEditing}
          error={validation.code}
          inputProps={{ maxLength: 64 }}
          onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
        />
        <TextField
          fullWidth
          margin="normal"
          size="small"
          type="email"
          label={t('Assigned email')}
          value={form.assignedEmail}
          error={validation.email}
          helperText={t('When set, the checkout email must match this address.')}
          inputProps={{ maxLength: 256 }}
          onChange={(event) => setForm((current) => ({ ...current, assignedEmail: event.target.value }))}
        />
        <TextField
          required
          fullWidth
          margin="normal"
          size="small"
          type="number"
          label={t('Maximum redemptions')}
          value={form.maxRedemptions}
          error={validation.maxRedemptions}
          inputProps={{ min: Math.max(1, currentUsage), step: 1 }}
          onChange={(event) => setForm((current) => ({ ...current, maxRedemptions: event.target.value }))}
        />
        {currentUsage > 0 && (
          <FormHelperText>
            {t('This code already has {{count}} reserved or redeemed uses.', { count: currentUsage })}
          </FormHelperText>
        )}
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={form.active}
              onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))}
            />
          }
          label={t('Enabled')}
        />
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button fullWidth size="small" variant="outlined" onClick={handleClose} disabled={isSaving}>
            {t('Cancel')}
          </Button>
          <Button fullWidth size="small" type="submit" variant="contained" disabled={isInvalid || isSaving}>
            {t(isEditing ? 'Save' : 'Add code')}
            {isSaving && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
